import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ShieldCheck, Plus, X, Lock, Users, Trash2, Save, Edit2 } from "lucide-react";

/**
 * /admin/access-control — role + permission matrix editor.
 *
 * Left: role list (system roles pinned, non-deletable + non-renamable).
 * Right: permission checkboxes grouped by area.
 *
 * Super-Admin is fully locked (permissions come from source code).
 */
export default function AdminAccessControl() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/roles"),
        api.get("/permissions/catalogue"),
      ]);
      setRoles(r1.data);
      setPermissions(r2.data.permissions);
      if (!activeRole && r1.data.length > 0) {
        setActiveRole(r1.data[0]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load roles");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grouped = useMemo(() => {
    const map = {};
    permissions.forEach((p) => {
      map[p.group] = map[p.group] || [];
      map[p.group].push(p);
    });
    return map;
  }, [permissions]);

  const toggle = (key) => {
    if (!activeRole || activeRole.key === "super_admin") return;
    const has = activeRole.permissions.includes(key);
    setActiveRole({
      ...activeRole,
      permissions: has ? activeRole.permissions.filter((p) => p !== key) : [...activeRole.permissions, key],
    });
    setDirty(true);
  };

  const save = async () => {
    if (!activeRole || !dirty) return;
    setSaving(true);
    try {
      await api.patch(`/admin/roles/${activeRole.role_id}`, {
        name: activeRole.name,
        description: activeRole.description,
        permissions: activeRole.permissions,
      });
      toast.success("Role saved");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!activeRole || activeRole.system) return;
    if (!confirm(`Delete role "${activeRole.name}"?`)) return;
    try {
      await api.delete(`/admin/roles/${activeRole.role_id}`);
      toast.success("Deleted");
      setActiveRole(null);
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-access-control">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="overline">Security</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Access Control</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">Define who can do what across PowerBuilt.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-accent text-sm" data-testid="acl-new-role">
          <Plus className="w-4 h-4 mr-1.5" /> New role
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* Role list */}
        <div className="space-y-2" data-testid="acl-role-list">
          {roles.map((r) => (
            <button
              key={r.role_id}
              onClick={() => { setActiveRole(r); setDirty(false); }}
              data-testid={`acl-role-${r.role_id}`}
              aria-pressed={activeRole?.role_id === r.role_id}
              className={`w-full text-left rounded-lg border p-3 transition ${
                activeRole?.role_id === r.role_id
                  ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-amber-900/10 shadow-sm"
                  : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356] bg-white dark:bg-slate-900"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="font-medium text-[#0A2540] dark:text-white text-sm truncate flex-1">{r.name}</div>
                {r.system && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 uppercase tracking-wider font-semibold">System</span>}
              </div>
              <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-0.5 truncate">{r.description}</div>
              <div className="flex items-center gap-3 mt-2 text-[10px] text-[#94A3B8]">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {r.permissions.length} perm</span>
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {r.user_count} user{r.user_count !== 1 && "s"}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        {!activeRole ? (
          <div className="card-pb p-12 text-center text-sm text-[#475569]">Pick a role on the left.</div>
        ) : (
          <div className="card-pb p-5" data-testid="acl-role-detail">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {activeRole.system ? (
                    <h2 className="font-heading text-xl text-[#0A2540] dark:text-white">{activeRole.name}</h2>
                  ) : (
                    <input value={activeRole.name} onChange={(e) => { setActiveRole({ ...activeRole, name: e.target.value }); setDirty(true); }}
                           data-testid="acl-role-name"
                           className="font-heading text-xl text-[#0A2540] dark:text-white bg-transparent border-b border-transparent hover:border-[#C9A356] focus:border-[#C9A356] outline-none w-full" />
                  )}
                  {activeRole.key === "super_admin" && <Lock className="w-4 h-4 text-[#94A3B8]" aria-label="Locked" />}
                </div>
                {activeRole.system ? (
                  <p className="text-xs text-[#475569] dark:text-slate-400 mt-1">{activeRole.description}</p>
                ) : (
                  <textarea value={activeRole.description || ""} rows="1"
                            onChange={(e) => { setActiveRole({ ...activeRole, description: e.target.value }); setDirty(true); }}
                            className="text-xs text-[#475569] dark:text-slate-400 bg-transparent w-full outline-none resize-none mt-1"
                            placeholder="Describe this role…" />
                )}
              </div>
              <div className="flex gap-2">
                {!activeRole.system && (
                  <button onClick={del} className="text-rose-500 text-sm inline-flex items-center hover:underline" data-testid="acl-delete-role">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </button>
                )}
                <button onClick={save} disabled={!dirty || saving || activeRole.key === "super_admin"}
                        className="btn-accent text-sm disabled:opacity-50"
                        data-testid="acl-save-role">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            {activeRole.key === "super_admin" && (
              <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 text-xs text-[#475569] mb-4">
                <Lock className="w-3.5 h-3.5 inline mr-1" /> Super Admin permissions are locked. Only role membership can be changed.
              </div>
            )}

            <div className="space-y-4">
              {Object.entries(grouped).map(([group, perms]) => (
                <div key={group}>
                  <div className="overline mb-2">{group}</div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {perms.map((p) => {
                      const on = activeRole.permissions.includes(p.key);
                      const locked = activeRole.key === "super_admin";
                      return (
                        <label
                          key={p.key}
                          data-testid={`perm-${p.key}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
                            on
                              ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-amber-900/10"
                              : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356]"
                          } ${locked ? "cursor-not-allowed opacity-70" : ""}`}
                        >
                          <input type="checkbox" checked={on} onChange={() => toggle(p.key)} disabled={locked}
                                 className="accent-[#C9A356]" data-testid={`perm-toggle-${p.key}`} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-[#0A2540] dark:text-white truncate">{p.label}</div>
                            <div className="text-[10px] font-mono text-[#94A3B8] truncate">{p.key}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(r) => { setCreating(false); setActiveRole(r); load(); }} />}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/roles", { name, description, permissions: [] });
      toast.success("Role created");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="acl-create-modal">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">New role</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#475569]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">Name<span className="text-rose-500 ml-0.5">*</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Field Supervisor" data-testid="acl-new-name"
                   className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/40" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3"
                      placeholder="Short-hand of who fits this role" data-testid="acl-new-desc"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/40" />
          </div>
          <p className="text-[11px] text-[#94A3B8]">You'll pick permissions in the next screen after creating.</p>
        </div>
        <div className="p-5 border-t border-[#E2E8F0] dark:border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={create} disabled={saving || !name.trim()} className="btn-accent text-sm" data-testid="acl-create-submit">
            {saving ? "Creating…" : "Create role"}
          </button>
        </div>
      </div>
    </div>
  );
}
